// Extend the Window interface for onNativeMessage
import { useDrawerStore, useMediaSettingsStore, useRoomActionsStore, useUiActionsStore } from '@src/stores';
import { sendMessageToAndroid } from './sender';

import { EWebViewEvents, IWebViewEvent } from './types';

declare global {
  interface Window {
    handleNativeMessage?: (raw: string) => void;
  }
}

// Handler for START_MEET
function handleStartMeet(msg: IWebViewEvent) {
  // Only handle the request variant
  if (msg && msg.payload && 'id' in msg.payload && 'meetName' in msg.payload) {
    const { id, meetName } = msg.payload;
    if (meetName) {
      const meetLink = `${window.location.origin}/join/?room=${id}&roomTitle=${encodeURIComponent(meetName)}`;
      sendMessageToAndroid({
        type: EWebViewEvents.START_MEET,
        status: 'SUCCESS',
        payload: {
          id,
          url: meetLink,
          meetName,
        },
      });
    } else {
      sendMessageToAndroid({
        type: EWebViewEvents.START_MEET,
        status: 'ERROR',
        payload: {
          id,
          code: '404',
          message: 'Meeting not found',
        },
      });
    }
  }
}

function handleMic(msg: IWebViewEvent) {
  const { onMicClick } = useRoomActionsStore.getState().micSettings;
  const { isAudioAllowed } = useMediaSettingsStore.getState().mediaSettings;
  try {
    if (!isAudioAllowed) {
      return;
    }
    switch (msg.type) {
      case EWebViewEvents.MUTE_MIC:
        onMicClick(true);
        break;
      case EWebViewEvents.UNMUTE_MIC:
        onMicClick(false);
        break;
    }
  } catch {}
}

function handleCam(msg: IWebViewEvent) {
  const { onCamClick } = useRoomActionsStore.getState().camSettings;
  const { isVideoAllowed } = useMediaSettingsStore.getState().mediaSettings;
  try {
    if (!isVideoAllowed) {
      return;
    }
    switch (msg.type) {
      case EWebViewEvents.VIDEO_ON:
        onCamClick(false);
        break;
      case EWebViewEvents.VIDEO_OFF:
        onCamClick(true);
        break;
    }
  } catch {}
}

function handleEnterPip(msg: IWebViewEvent) {
  const { isCamClosed } = useRoomActionsStore.getState().camSettings;
  const { isMicClosed } = useRoomActionsStore.getState().micSettings;

  sendMessageToAndroid({
    type: EWebViewEvents.MIC_AND_CAMEAR_STATES,
    payload: {
      id: msg.payload.id,
      isMicEnabled: !isMicClosed,
      isCameraEnabled: !isCamClosed,
    },
  });

  useRoomActionsStore.getState().pipSettings.onClick(true);
}

function handleExitPip(msg: IWebViewEvent) {
  useRoomActionsStore.getState().pipSettings.onClick(false);
}

function handleExitMobileSheet(_msg: IWebViewEvent) {
  const { selectedKey, isDrawerOpen, toggleDrawer } = useDrawerStore.getState();
  if (selectedKey === 'virtual-background' && isDrawerOpen) {
    toggleDrawer(false);
  }
  useUiActionsStore.getState().setHideMobileDrawer(true);
}

// Map of message types to handlers
export const messageHandlers: Record<string, (msg: IWebViewEvent) => void> = {
  'meet/v1/START_MEET': handleStartMeet,
  'pip/v1/MUTE_MIC': handleMic,
  'pip/v1/UNMUTE_MIC': handleMic,
  'pip/v1/VIDEO_ON': handleCam,
  'pip/v1/VIDEO_OFF': handleCam,
  'pip/v1/ENTER_PIP': handleEnterPip,
  'pip/v1/EXIT_PIP': handleExitPip,
  'pageConfig/v1/DISMISS_WEB_SHEET': handleExitMobileSheet,

  // Add more handlers here as needed
};

export const handleNativeMessage = (msg: any) => {
  try {
    const handler = messageHandlers[msg.type];
    if (handler) {
      handler(msg);
    } else {
      console.warn('Mobile: No handler for message type:', msg.type);
    }
  } catch (e) {
    console.error('Mobile: Invalid native message:', msg);
  }
};

export function setupNativeMessageListener() {
  window.handleNativeMessage = handleNativeMessage;
}
